/**
 * glør Final-CTA — Kamin-Auswahl + auswählbare Set-Karten + EIN Add-to-Cart.
 *
 * PRODUKT-PFAD (einziger Weg): Ein verbundenes Kamin-Produkt liefert Größe +
 * Farbe als echte Produktoptionen. Die Optionsradios (Pills/Swatches) bestimmen
 * über eine eingebettete Varianten-Matrix die aktive Variante. Der angezeigte
 * Karten-Preis ist standardmäßig der Preis dieser Variante (Server-formatierter
 * String), optional je Karte per Override überschrieben.
 *
 * Ist KEIN Produkt verbunden, funktionieren die Karten über ihren vorhandenen
 * Produkt-/URL-Fallback (Karten-Variante bzw. Ziel-URL) — nichts crasht.
 *
 * Gemeinsam: Karten-Auswahl per Radio (kein Reload), Minimierung inaktiver
 * Karten, „N items“, AJAX-Add mehrerer Items + explizites Öffnen des
 * Cart-Drawers (Muster aus assets/glr-cart-set.js).
 */

/** @param {HTMLElement} root */
function initRoot(root) {
  const radios = /** @type {HTMLInputElement[]} */ (
    Array.from(root.querySelectorAll('[data-glr-fcta-radio]'))
  );
  if (radios.length === 0) return;

  const addBtn = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-glr-fcta-add]'));
  const addLabel = root.querySelector('[data-glr-fcta-add-label]');

  const selectedSet = () => radios.find((r) => r.checked) || radios[0];
  /** @param {HTMLInputElement} radio */
  const cardOf = (radio) => radio.closest('.glr-final-cta__card');

  // --- Produkt-Pfad: Varianten-Matrix + Options-Radios -----------------------
  const variantsEl = root.querySelector('[data-glr-fcta-variants]');
  /** @type {{id:number, options:string[], available:boolean, price:string, compare:string, image:string}[]} */
  let matrix = [];
  if (variantsEl) {
    try {
      matrix = JSON.parse(variantsEl.textContent || '[]') || [];
    } catch (e) {
      matrix = [];
    }
  }
  const optRadios = /** @type {HTMLInputElement[]} */ (
    Array.from(root.querySelectorAll('[data-glr-fcta-opt-radio]'))
  );
  const hasProduct = matrix.length > 0 && optRadios.length > 0;
  const optIndexes = hasProduct
    ? Array.from(new Set(optRadios.map((r) => Number(r.dataset.optIndex)))).sort((a, b) => a - b)
    : [];

  /** @param {number} i */
  const groupOf = (i) => optRadios.filter((r) => Number(r.dataset.optIndex) === i);

  /** Aktuell gewählte Optionswerte (ein Wert pro Optionsgruppe). */
  const selectedValues = () =>
    optIndexes.map((i) => {
      const g = groupOf(i);
      const c = g.find((r) => r.checked) || g.find((r) => !r.disabled) || g[0];
      return c ? c.dataset.optValue : null;
    });

  /** @param {(string|null)[]} vals */
  const findVariant = (vals) => matrix.find((v) => vals.every((val, i) => v.options[i] === val));
  const firstAvailable = () => matrix.find((v) => v.available) || matrix[0];
  const productVariant = () => findVariant(selectedValues());

  /** Radios auf die Optionswerte einer Variante setzen (inkl. is-active). */
  const setRadiosToVariant = (variant) => {
    if (!variant) return;
    for (const i of optIndexes) {
      const val = variant.options[i];
      for (const r of groupOf(i)) {
        r.checked = r.dataset.optValue === val;
        const w = r.closest('.glr-final-cta__fp-opt, .glr-final-cta__color-opt');
        if (w) w.classList.toggle('is-active', r.checked);
      }
    }
  };

  /** Verfügbarkeit je Optionswert relativ zur restlichen Auswahl aktualisieren. */
  const updateAvailability = () => {
    const vals = selectedValues();
    for (const i of optIndexes) {
      for (const r of groupOf(i)) {
        const trial = vals.slice();
        trial[i] = r.dataset.optValue;
        const v = matrix.find((m) => trial.every((val, k) => m.options[k] === val));
        const ok = Boolean(v && v.available);
        r.disabled = !ok;
        const w = r.closest('.glr-final-cta__fp-opt, .glr-final-cta__color-opt');
        if (w) w.classList.toggle('is-disabled', !ok);
      }
    }
  };

  /** Box-Hero-Bild der gewählten Variante folgen lassen (nur mit Produkt). */
  const applyBoxImage = (variant) => {
    if (!hasProduct || !variant) return;
    const fpId = root.dataset.fpProduct;
    if (!fpId) return;
    if (variant.image) {
      root.querySelectorAll('[data-glr-fcta-hero-img]').forEach((img) => {
        if (img instanceof HTMLImageElement && img.dataset.setProduct === fpId) {
          img.src = variant.image;
        }
      });
    }
    if (variant.title) {
      root.querySelectorAll('[data-glr-fcta-hero-name]').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.setProduct === fpId) {
          el.textContent = variant.title;
        }
      });
    }
  };

  /** Karten-Preise auf die aktive Variante schreiben — außer bei Override. */
  const applyProductPrices = (variant) => {
    if (!variant) return;
    for (const r of radios) {
      const card = cardOf(r);
      if (!card) continue;
      const priceEl = card.querySelector('[data-glr-fcta-price]');
      const compareEl = card.querySelector('[data-glr-fcta-compare]');
      const pOv = (card.dataset.priceOverride || '').trim();
      const cOv = (card.dataset.compareOverride || '').trim();
      if (priceEl && !pOv) priceEl.textContent = variant.price;
      if (compareEl && !cOv) {
        if (variant.compare) {
          compareEl.textContent = variant.compare;
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
    }
  };

  /**
   * Aktive, in den Warenkorb zu legende Varianten-ID (Produkt-Pfad).
   * Ohne verbundenes Produkt leer → Karten-Fallback (Variante/URL) greift.
   * @returns {string}
   */
  const activeVariantId = () => {
    if (hasProduct) {
      const v = productVariant();
      return v ? String(v.id) : '';
    }
    return '';
  };

  /** Angezeigter Preis einer Karte (Override → Variantenpreis). */
  const displayedPriceOf = (card, variant) => {
    if (!card) return '';
    const pOv = (card.dataset.priceOverride || '').trim();
    if (pOv) return pOv;
    if (hasProduct) return variant ? variant.price : '';
    return '';
  };

  const apply = () => {
    const sel = selectedSet();
    for (const r of radios) {
      const card = cardOf(r);
      if (card) card.classList.toggle('is-active', r === sel);
    }

    let variant = null;
    if (hasProduct) {
      variant = productVariant();
      if (!variant || !variant.available) {
        variant = firstAvailable();
        setRadiosToVariant(variant);
      }
      updateAvailability();
      applyProductPrices(variant);
      applyBoxImage(variant);
    }

    if (!addBtn) return;

    const name = sel?.dataset.name?.trim() ?? '';
    let price = displayedPriceOf(cardOf(sel), variant);
    if (!price) price = sel?.dataset.price?.trim() ?? '';
    const tpl = addBtn.dataset.addTemplate || 'Add {set} to cart';

    let text = tpl.replace('{set}', name).trim();
    if (price) text += ` · ${price}`;
    if (addLabel) addLabel.textContent = text;
    else addBtn.textContent = text;

    // Produkt-Pfad ermittelt die Variante direkt über activeVariantId();
    // ohne Produkt merkt sich der Button die Karten-Variante bzw. Ziel-URL.
    addBtn.dataset.variantId = hasProduct ? '' : sel?.value ?? '';
    addBtn.dataset.url = sel?.dataset.url ?? addBtn.dataset.fallbackUrl ?? '';
  };

  root.addEventListener('change', (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.matches('[data-glr-fcta-opt-radio]')) {
      const i = t.dataset.optIndex;
      for (const r of optRadios) {
        if (r.dataset.optIndex !== i) continue;
        const w = r.closest('.glr-final-cta__fp-opt, .glr-final-cta__color-opt');
        if (w) w.classList.toggle('is-active', r.checked);
      }
      apply();
    } else if (t.matches('[data-glr-fcta-radio]')) {
      apply();
    }
  });

  if (addBtn) {
    addBtn.addEventListener('click', () => {
      const sel = selectedSet();

      // Produkt-Pfad: aktive Variante (Größe + Farbe) + feste Set-Extras.
      if (hasProduct) {
        const variantId = activeVariantId();
        if (!variantId) return;
        const items = [{ id: variantId, quantity: 1 }];
        for (const ex of parseExtras(cardOf(sel)?.dataset.extras || '')) items.push(ex);
        for (const ad of parseExtras(cardOf(sel)?.dataset.addons || '')) items.push(ad);
        const discount = (cardOf(sel)?.dataset.discount || '').trim();
        // Set aus Kamin + Add-ons als ein Bundle markieren (Cart-Transform-Anker),
        // analog zu assets/glr-pdp-bundle.js. Nur bei echten Sets (>1 Item).
        if (items.length > 1) tagBundleItems(items, sel?.dataset.name);
        addItemsToCart(items, addBtn, discount);
        return;
      }

      // Kein verbundenes Produkt → Karten-Variante bzw. URL.
      const variantId = addBtn.dataset.variantId;
      if (!variantId) {
        if (addBtn.dataset.url) window.location.assign(addBtn.dataset.url);
        return;
      }
      addItemsToCart([{ id: variantId, quantity: 1 }], addBtn);
    });
  }

  apply();
}

/**
 * "id:menge,id:menge" → Items. Leere/kaputte Einträge werden verworfen.
 * @param {string} raw
 * @returns {{id:string, quantity:number}[]}
 */
function parseExtras(raw) {
  return (raw || '')
    .trim()
    .split(',')
    .map((part) => {
      const [id, qty] = part.split(':');
      return { id: (id || '').trim(), quantity: parseInt(qty, 10) || 1 };
    })
    .filter((it) => it.id);
}

/**
 * Markiert alle Items als EIN Bundle (Cart-Transform-Anker) — exakt nach dem
 * Muster von assets/glr-pdp-bundle.js: gemeinsame gid an alle Items,
 * erstes Item (Kamin/Hauptprodukt) = "parent", der Rest = "child";
 * optionaler Set-Titel am Parent. Underscore-Props = fuer den Kunden unsichtbar.
 * @param {{id:string, quantity:number, properties?:Object}[]} items
 * @param {string} [title] Set-Name (aus data-name der gewaehlten Karte).
 */
function tagBundleItems(items, title) {
  if (!Array.isArray(items) || items.length === 0) return;
  const gid = 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  const setTitle = (title || '').trim();
  items.forEach((it, i) => {
    it.properties = Object.assign({}, it.properties, {
      _glr_bundle: gid,
      _glr_bundle_role: i === 0 ? 'parent' : 'child',
    });
    if (i === 0 && setTitle) it.properties._glr_bundle_title = setTitle;
  });
}

/**
 * Legt mehrere Varianten per AJAX in den Warenkorb und öffnet den Cart-Drawer —
 * nach dem bewährten Muster von assets/glr-cart-set.js.
 * @param {{id:string, quantity:number}[]} items
 * @param {HTMLButtonElement} btn
 * @param {string} [discount] Rabatt-Code, der nach dem Add angewandt wird.
 */
async function addItemsToCart(items, btn, discount) {
  if (!items.length || !btn || btn.classList.contains('is-loading')) return;
  btn.classList.add('is-loading');
  btn.setAttribute('aria-busy', 'true');

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter((id) => Boolean(id));

  /** @type {{items:{id:string,quantity:number,properties?:Object}[], sections?:string}} */
  const body = {
    items: items.map((i) => {
      const line = { id: i.id, quantity: i.quantity };
      if (i.properties) line.properties = i.properties;
      return line;
    }),
  };
  if (sectionIds.length) body.sections = sectionIds.join(',');

  let CartLinesUpdateEvent = null;
  let deferred = null;
  try {
    ({ CartLinesUpdateEvent } = await import('@shopify/events'));
    deferred = CartLinesUpdateEvent.createPromise();
    btn.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: i.id, quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
  } catch (e) {
    /* @shopify/events nicht verfügbar — Add + Drawer-Open laufen trotzdem. */
  }

  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();

    if (data.status) {
      if (deferred) deferred.reject?.(new Error(data.description || data.message || 'Add failed'));
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    // Optional: Rabattcode je Set auf den Warenkorb anwenden (Muster aus glr-cart-set.js).
    if (discount) {
      try {
        const dbody = { discount };
        if (sectionIds.length) dbody.sections = sectionIds.join(',');
        await fetch(Theme.routes.cart_update_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(dbody),
        });
      } catch (e) {
        console.warn('[glr-final-cta] Rabattcode konnte nicht angewendet werden:', e);
      }
    }

    if (deferred && CartLinesUpdateEvent) {
      const cartRes = await fetch(`${Theme.routes.cart_url}.js`);
      const ajaxCart = await cartRes.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'glr-final-cta',
          sections: data.sections,
          didError: false,
        },
      });
    }

    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  } catch (err) {
    console.error('[glr-final-cta] Add to cart fehlgeschlagen:', err);
  } finally {
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
  }
}

document.querySelectorAll('[data-glr-fcta-root]').forEach((root) => {
  if (root instanceof HTMLElement) initRoot(root);
});
